import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../icon/Icon';
import { Tooltip } from '../tooltip/Tooltip';
import { useSpring, animated } from 'react-spring/web.cjs';
import './Dropdown.scss';

type item = {
  id: number;
  label: string;
};

interface DropdownProps {
  disabled?: boolean;
  information?: boolean | string;
  onChange: (selectedValue: string) => any;
  options: item[];
  label: string;
  placeholder?: boolean | string;
  selectedValue: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  disabled,
  information,
  onChange,
  options,
  label,
  placeholder,
  selectedValue
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const node = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [showOptions]);

  const handleClick = (e: any) => {
    if (null !== node.current) {
      if (node.current.contains(e.target)) {
        return;
      } else if (showOptions) {
        setShowOptions(!showOptions);
      }
    }
  };

  const handleChange = (selectedValue: string) => {
    onChange(selectedValue);
    setShowOptions(!showOptions);
  };

  const animateIcon = useSpring({
    config: {
      tension: 200,
      friction: 20,
      clamp: true
    },
    transform: showOptions ? 'rotate(180deg)' : 'rotate(0deg)'
  });

  return (
    <div className="ads-dropdown" ref={node} data-testid="ads-dropdown">
      <div className="ads-dropdown__label" data-testid="ads-dropdown__label">
        {label}
        {information && (
          <Tooltip
            content={`${information}`}
            ariaName="Dropdown information tooltip"
          />
        )}
      </div>
      <div
        aria-disabled={disabled ? true : false}
        data-testid="ads-dropdown__default"
        className={`ads-dropdown__default ${
          disabled ? 'ads-dropdown--disabled' : ''
        }`}
        onClick={() => setShowOptions(!showOptions)}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === ' ') {
            setShowOptions(!showOptions);
          }
        }}
        tabIndex={0}
      >
        {selectedValue ? (
          <span className="ads-dropdown__value">{selectedValue}</span>
        ) : placeholder ? (
          <span
            className="ads-dropdown__placeholder"
            data-testid="ads-dropdown__placeholder-default"
            aria-placeholder={`${placeholder}`}
          >
            {placeholder}
          </span>
        ) : (
          <span
            className="ads-dropdown__placeholder"
            data-testid="ads-dropdown__placeholder"
          >
            {onChange(options[0].label)}
          </span>
        )}
        <animated.span style={animateIcon}>
          {disabled ? (
            <Icon name="arrowDownLg" color="ads-color-disabled" />
          ) : (
            <Icon name="arrowDownLg" />
          )}
        </animated.span>
      </div>
      {showOptions && (
        <div className="ads-dropdown__list">
          {options.map((option) => (
            <div
              className="ads-dropdown__list-item"
              data-testid="ads-dropdown__list-item"
              key={option.id}
              onClick={() => handleChange(option.label)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === ' ') {
                  handleChange(option.label);
                }
              }}
              tabIndex={0}
            >
              <span className="ads-dropdown__item">
                <span>{option.label}</span>
                <span>
                  {selectedValue === option.label ? (
                    <Icon name="checkmarkLg" />
                  ) : null}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
